import { type Editor, useCurrentEditor } from '@tiptap/react';
import { Copy, ExternalLink, Link as LinkIcon, Pencil, Unlink } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../button/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../dropdown-menu/dropdown-menu';
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from '../../popover/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../tooltip/tooltip';
import { applyLink } from './link-commands';
import { normalizeLinkHref } from './link-href';

interface LinkButtonProps {
  active: boolean;
}

export const LinkButton = ({ active }: LinkButtonProps) => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return active ? <LinkActionsButton editor={editor} /> : <LinkInsertButton editor={editor} />;
};

interface BranchProps {
  editor: Editor;
}

const LinkInsertButton = ({ editor }: BranchProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [href, setHref] = useState('');

  const populateFromSelection = useCallback(() => {
    const { from, to } = editor.state.selection;
    setHref('');
    setText(from === to ? '' : editor.state.doc.textBetween(from, to, ' '));
  }, [editor]);

  const handleOpenChange = (next: boolean) => {
    if (next && !open) {
      populateFromSelection();
    }
    setOpen(next);
  };

  const insert = () => {
    if (!applyLink(editor, { href, text })) {
      return;
    }
    setOpen(false);
  };

  const canInsert = useMemo(() => normalizeLinkHref(href) !== null, [href]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={<PopoverTrigger />}
          className="tiptap-toolbar-button"
          aria-label={t('insert-link')}
          onMouseDown={(event) => event.preventDefault()}
        >
          <LinkIcon />
        </TooltipTrigger>
        <TooltipContent>{t('insert-link')}</TooltipContent>
      </Tooltip>
      <PopoverContent side="bottom" align="start" className="w-80 gap-3">
        <LinkForm
          title={t('insert-link')}
          text={text}
          href={href}
          onTextChange={setText}
          onHrefChange={setHref}
          canSubmit={canInsert}
          submitLabel={t('insert')}
          onSubmit={insert}
          onCancel={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
};

const LinkActionsButton = ({ editor }: BranchProps) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [text, setText] = useState('');
  const [href, setHref] = useState('');

  const populateFromLink = useCallback(() => {
    const attrs = editor.getAttributes('link');
    setHref((attrs.href as string) ?? '');
    // Snap selection to the full link range so the prefilled text matches the anchor's
    // content regardless of where the cursor sat inside the link.
    editor.chain().extendMarkRange('link').run();
    const { from, to } = editor.state.selection;
    setText(editor.state.doc.textBetween(from, to, ' ').trim());
  }, [editor]);

  const openEdit = () => {
    populateFromLink();
    setPopoverOpen(true);
  };

  const submitEdit = () => {
    if (!applyLink(editor, { href, text })) {
      return;
    }
    setPopoverOpen(false);
  };

  const canSubmit = useMemo(() => normalizeLinkHref(href) !== null, [href]);

  const copy = async () => {
    const current = (editor.getAttributes('link').href as string | undefined) ?? '';
    try {
      await navigator.clipboard.writeText(current);
    } catch {
      // best-effort; silently fail if clipboard API is unavailable
    }
  };

  const open = () => {
    const current = (editor.getAttributes('link').href as string | undefined) ?? '';
    const target = normalizeLinkHref(current);
    if (!target) {
      return;
    }
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger
            ref={triggerRef}
            render={<DropdownMenuTrigger />}
            className="tiptap-toolbar-button"
            aria-label={t('insert-link')}
            aria-pressed={true}
            onMouseDown={(event) => event.preventDefault()}
          >
            <LinkIcon />
          </TooltipTrigger>
          <TooltipContent>{t('insert-link')}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent side="bottom" align="start" className="w-fit">
          <DropdownMenuItem onClick={openEdit}>
            <Pencil />
            {t('edit-link')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={copy}>
            <Copy />
            {t('copy-link')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={open}>
            <ExternalLink />
            {t('open-link')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={remove}>
            <Unlink />
            {t('remove-link')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Controlled popover with no trigger of its own - anchored to the toolbar button so
       the edit form opens against it when the dropdown's "Edit" item is clicked. */}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverContent side="bottom" align="start" anchor={triggerRef} className="w-80 gap-3">
          <LinkForm
            title={t('edit-link')}
            text={text}
            href={href}
            onTextChange={setText}
            onHrefChange={setHref}
            canSubmit={canSubmit}
            submitLabel={t('insert')}
            onSubmit={submitEdit}
            onCancel={() => setPopoverOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

interface LinkFormProps {
  title: string;
  text: string;
  href: string;
  onTextChange: (value: string) => void;
  onHrefChange: (value: string) => void;
  canSubmit: boolean;
  submitLabel: string;
  onSubmit: () => void;
  onCancel: () => void;
}

const LinkForm = ({
  title,
  text,
  href,
  onTextChange,
  onHrefChange,
  canSubmit,
  submitLabel,
  onSubmit,
  onCancel,
}: LinkFormProps) => {
  const { t } = useTranslation();
  return (
    <>
      <PopoverHeader>
        <PopoverTitle>{title}</PopoverTitle>
      </PopoverHeader>
      <label className="flex flex-col gap-1 text-foreground text-xs">
        {t('text-to-display')}
        <input
          type="text"
          value={text}
          onChange={(event) => onTextChange(event.target.value)}
          className="rounded-sm border border-input bg-background px-2 py-1 text-foreground text-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
        />
      </label>
      <label className="flex flex-col gap-1 text-foreground text-xs">
        {t('address')}
        <input
          type="url"
          value={href}
          onChange={(event) => onHrefChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onSubmit();
            }
          }}
          placeholder="https://..."
          spellCheck={false}
          className="rounded-sm border border-input bg-background px-2 py-1 text-foreground text-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
        />
      </label>
      <div className="flex justify-end gap-2">
        <Button type="default" fullWidth={false} onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button type="primary" fullWidth={false} disabled={!canSubmit} onClick={onSubmit}>
          {submitLabel}
        </Button>
      </div>
    </>
  );
};
